import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { CommonModule } from '@angular/common';

import { DropdownToggleHelper } from '../../helpers/dropdown-toggle.helper';
export interface DateRangeChange {
  startDate: Date | null;
  endDate: Date | null;
  error: string;
}

@Component({
  selector: 'app-wi-date-range-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wi-date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./wi-date-range-picker.component.scss'],
})
export class DateRangePickerComponent implements OnChanges, OnInit {
  private host = inject(ElementRef<HTMLElement>);
  private cdr = inject(ChangeDetectorRef);
  // modes
  @Input() mode: 'date' | 'fixed' | 'monthly' = 'fixed';
  // single date mode
  @Input() selectedDate: Date | null = null;
  // range mode
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() minDays = 1;
  @Input() maxDays = 30;
  @Input() placeholder = 'Select date';

  // monthly mode
  @Input() selectedMonth!: number;
  @Input() selectedYear!: number;
  @Input() inline = false;
  @Input() showActions = true;
  @Input() showInlineValidation = true;
  @Input() disableDate?: (date: Date) => boolean;

  @Output() dateChange = new EventEmitter<Date | null>();

  @Output() rangeChange = new EventEmitter<DateRangeChange>();

  @Output()
  monthYearChange = new EventEmitter<{
    month: number;
    year: number;
  }>();

  /** Emits when the popover opens or closes so parents can keep sibling popovers exclusive. */
  @Output() openedChange = new EventEmitter<boolean>();

  readonly dropdownId = `date-${Math.random().toString(36).slice(2)}`;

  get open(): boolean {
    return DropdownToggleHelper.isOpen(this.dropdownId);
  }

  monthCursor = new Date();
  calendarDays: Date[] = [];
  readonly weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  readonly monthLabels = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  @ViewChild('panelRef') panelRef?: ElementRef<HTMLElement>;

  panelStyles: Record<string, string> = {};

  constructor() {
    this.buildCalendarDays();
  }

  ngOnInit() {
    if (this.inline) {
      DropdownToggleHelper.open(this.dropdownId);
    }

    this.buildCalendarDays();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedMonth'] || changes['selectedYear']) {
      if (this.selectedMonth && this.selectedYear) {
        // ✅ FIX HERE
        this.monthCursor = new Date(this.selectedYear, this.selectedMonth - 1, 1);

        this.buildCalendarDays();
      }
    }
  }

  get monthLabel(): string {
    return this.monthCursor.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  pretty(d: Date | null): string {
    if (!d) {
      return '';
    }

    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get displayValue(): string {
    if (this.mode === 'date') {
      return this.pretty(this.selectedDate);
    }

    if (!this.startDate && !this.endDate) {
      return '';
    }

    if (this.startDate && this.endDate) {
      return `${this.pretty(this.startDate)} - ${this.pretty(this.endDate)}`;
    }

    return this.startDate ? `${this.pretty(this.startDate)} -` : '';
  }

  get canApply(): boolean {
    if (this.mode === 'date') {
      return !!this.selectedDate;
    }

    if (this.mode === 'monthly') {
      return !!this.selectedMonth && !!this.selectedYear;
    }

    if (this.mode === 'fixed') {
      if (!this.startDate || !this.endDate) {
        return false;
      }

      return !this.validate().error;
    }

    return false;
  }

  toggleOpen() {
    if (this.inline) {
      return;
    }

    const opened = DropdownToggleHelper.toggle(this.dropdownId);
    this.openedChange.emit(opened);

    if (opened) {
      const referenceDate = this.mode === 'date' ? this.selectedDate : this.startDate;

      this.monthCursor = referenceDate ? new Date(referenceDate) : new Date();

      this.buildCalendarDays();

      this.cdr.detectChanges();

      requestAnimationFrame(() => {
        this.updatePanelPosition();
      });
    }
  }

  /** Closes the popover. Safe to call when already closed. */
  close(): void {
    if (this.inline || !DropdownToggleHelper.isOpen(this.dropdownId)) {
      return;
    }
    DropdownToggleHelper.close(this.dropdownId);
    this.openedChange.emit(false);
    this.cdr.detectChanges();
  }

  prevMonth() {
    const d = new Date(this.monthCursor);

    d.setMonth(d.getMonth() - 1);

    this.monthCursor = d;

    this.buildCalendarDays();

    if (this.mode === 'monthly') {
      this.emitMonth();
    }
  }

  nextMonth() {
    const d = new Date(this.monthCursor);
    d.setMonth(d.getMonth() + 1);
    this.monthCursor = d;
    this.buildCalendarDays();
    if (this.mode === 'monthly') {
      this.emitMonth();
    }
  }

  emitMonth() {
    this.monthYearChange.emit({
      month: this.selectedMonth,
      year: this.selectedYear,
    });
  }

  prevYear() {
    this.selectedYear--;
    this.emitMonth();
  }

  nextYear() {
    this.selectedYear++;
    this.emitMonth();
  }

  selectMonth(month: number) {
    // month = 0–11
    this.selectedMonth = month + 1;
    this.monthCursor = new Date(this.selectedYear, month, 1);
    this.emitMonth();
  }

  pickDate(day: Date) {
    const picked = this.strip(day);

    // SINGLE DATE
    if (this.mode === 'date') {
      this.selectedDate = picked;
      this.dateChange.emit(picked);
      this.close();
      return;
    }

    // RANGE
    if (!this.startDate || (this.startDate && this.endDate)) {
      this.startDate = picked;
      this.endDate = null;
      this.emitRange();
      return;
    }

    const start = this.strip(this.startDate);

    if (picked.getTime() < start.getTime()) {
      this.startDate = picked;
      this.endDate = null;
      this.emitRange();
      return;
    }

    this.endDate = picked;
    this.emitRange();
  }

  clear() {
    if (this.mode === 'date') {
      this.selectedDate = null;
      this.dateChange.emit(null);
      return;
    }

    this.startDate = null;
    this.endDate = null;

    this.emitRange();
  }

  apply() {
    if (this.mode === 'date') {
      this.close();
      return;
    }

    const { error } = this.validate();

    if (!error && this.startDate && this.endDate) {
      this.close();
    }

    this.emitRange();
  }

  emitRange() {
    const { error } = this.validate();

    this.rangeChange.emit({
      startDate: this.startDate,
      endDate: this.endDate,
      error,
    });
  }

  validate() {
    if (!this.startDate || !this.endDate) {
      return {
        error: '',
      };
    }

    const diff =
      Math.floor(
        (this.strip(this.endDate).getTime() - this.strip(this.startDate).getTime()) / 86400000
      ) + 1;

    if (diff < this.minDays || diff > this.maxDays) {
      return {
        error: `Please select between ${this.minDays} and ${this.maxDays} days`,
      };
    }

    return {
      error: '',
    };
  }

  inMonth(day: Date) {
    return day.getMonth() === this.monthCursor.getMonth();
  }

  isStart(day: Date) {
    return !!this.startDate && this.strip(this.startDate).getTime() === this.strip(day).getTime();
  }

  isEnd(day: Date) {
    return !!this.endDate && this.strip(this.endDate).getTime() === this.strip(day).getTime();
  }

  isInRange(day: Date) {
    if (!this.startDate || !this.endDate) {
      return false;
    }

    const t = this.strip(day).getTime();

    const s = this.strip(this.startDate).getTime();

    const e = this.strip(this.endDate).getTime();

    return t >= s && t <= e;
  }

  strip(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  buildCalendarDays() {
    const first = new Date(this.monthCursor.getFullYear(), this.monthCursor.getMonth(), 1);

    const offset = first.getDay();

    const start = new Date(first);

    start.setDate(first.getDate() - offset);

    this.calendarDays = Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);

      d.setDate(start.getDate() + i);

      return d;
    });
  }

  isDisabled(day: Date): boolean {
    return this.disableDate ? this.disableDate(this.strip(day)) : false;
  }

  /**  */
  updatePanelPosition(): void {
    if (!this.panelRef) {
      return;
    }

    const trigger = this.host.nativeElement.querySelector('.frp-trigger') as HTMLElement;

    const panel = this.panelRef.nativeElement;

    if (!trigger || !panel) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();

    const panelRect = panel.getBoundingClientRect();

    const gap = 8;

    let left = triggerRect.left;

    let top = triggerRect.bottom + gap;

    // viewport
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // right overflow
    if (left + panelRect.width > vw - 12) {
      left = vw - panelRect.width - 12;
    }

    // bottom overflow
    if (top + panelRect.height > vh - 12) {
      top = triggerRect.top - panelRect.height - gap;
    }

    // top overflow
    if (top < 12) {
      top = 12;
    }

    this.panelStyles = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: '3000',
    };

    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  onResize() {
    if (this.open) {
      requestAnimationFrame(() => {
        this.updatePanelPosition();
      });
    }
  }

  @HostListener('window:scroll')
  onScroll() {
    if (this.open) {
      requestAnimationFrame(() => {
        this.updatePanelPosition();
      });
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onOutsideClick(event: MouseEvent) {
    if (!DropdownToggleHelper.isOpen(this.dropdownId)) {
      return;
    }

    const target = event.target as Node;
    const clickedInside = this.host.nativeElement.contains(target);

    if (!clickedInside) {
      this.close();
    }
  }
}
